/**
 * Group of lists to be displayed in the overlay
 *
 * @module views/components/ListGroup
 * @memberof -Common
 */
import './ListGroup.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

/**
 * Represents a ListGroup component
 *
 * @method
 * @param { ListGroupProps } props - React properties passed from composition
 * @returns ListGroup
 */
export const ListGroup = ( props ) => {
  const {
    componentKey,
    items
  } = props;
  if( !hasItems( items ) ){
    return null;
  }

  return (
    <ListItems>
      {
        items.map( ( listItem, i ) => {
          return (
            <ListItem
              { ...spreadProps( { ...listItem } ) }
              key={ `${i}:${componentKey}:${listItem.title}` }
            />
          );
        } )
      }
    </ListItems>
  );
};

/**
 * Represents a ListItems component
 *
 * @method
 * @param { ListItemsProps } props - React properties passed from composition
 * @returns ListItems
 */
export const ListItems = ( props ) => {
  return ( <ul className='ListGroup' role='list' >{ props.children }</ul> );
};

/**
 * Represents a ListItem component
 *
 * @method
 * @param { ListItemProps } props - React properties passed from composition
 * @returns ListItem
 */
export const ListItem = ( props ) => {
  if( !props ){
    return;
  }
  return (
    <li
      className='ListGroup__Items'
    >
      <div className='ListGroup__ListItemIcon'>
        <Icon
          size='xl'
          name={ props.icon }
          aria-hidden={ true }
        />
      </div>
      <div className='ListGroup__ListItemTitle'>
        <div className='ListGroup__ListItemTitleRow' >
          <Text
            textStyle='body-lg'
            htmlTag='h2'
          >
            { props.title }
          </Text>
        </div>
        { props.subTitle && (
          <div className='ListGroup__ListItemTitleRow' >
            <Text
              textStyle='body'
              color='neutral-500'
              htmlTag='span'
            >
              { props.subTitle }
            </Text>
          </div>
        ) }
      </div>
    </li>
  );
};

/**
 * Property type definitions
 * @typedef ListGroupProps
 * @type {object}
 * @property {string} componentKey - Unique identifier for the component instance, useful for React keying and identifying elements.
 * @property {array} items - list items to display
 */
export const propTypes =  {
  componentKey: PropTypes.number,
  items: PropTypes.array
};

/**
 * Property type definitions
 * @typedef ListItemProps
 * @type {object}
 * @property {string} key - Unique identifier for the component instance, useful for React keying and identifying elements.
 * @property {String} icon - Icon name
 * @property {String} title - Title of List Item
 * @property {String} subTitle - Sub title of List Item
 */
export const listItemPropTypes =  {
  key: PropTypes.string,
  icon: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.string
};

ListGroup.propTypes = propTypes;
ListItem.propTypes = listItemPropTypes;

export default ListGroup;
